import React from 'react';

const EnvironmentContext = React.createContext<Record<string, unknown> | null>(null);

export const EnvironmentProvider: React.FC<{
    value: Record<string, unknown>;
    children: React.ReactNode;
}> = ({ value, children }) => (
    <EnvironmentContext.Provider value={value}>
        {children}
    </EnvironmentContext.Provider>
);

export function useEnvironment<T = Record<string, unknown>>(): T {
    const ctx = React.useContext(EnvironmentContext);
    if (!ctx) throw new Error('useEnvironment must be used within EnvironmentProvider');
    return ctx as T;
}

/**
 * Non-throwing variant: returns the surrounding environment, or an empty `{}`
 * when there's no `EnvironmentProvider` in scope. Use it where an environment
 * is optional — e.g. the UI-tool dispatcher resolves `env` for actions on
 * EVERY uiTool part, including chat surfaces that have no provider (there the
 * action's `env.foo?.()` calls simply no-op). On replay, the surrounding
 * `InertEnvironmentProvider` already neutralises every callable.
 */
export function useEnvironmentOptional<T = Record<string, unknown>>(): T {
    return (React.useContext(EnvironmentContext) ?? {}) as T;
}

/**
 * Replaces every callable property of the surrounding environment
 * with a no-op for `children`. Non-callable properties (data, refs,
 * primitive values) pass through unchanged. Use it to isolate
 * historical / replayed React subtrees — e.g. completed chat
 * messages whose body still carries live-TSX side-effect hooks like
 * `useDashboardPad` — so re-mounting them on session reload cannot
 * mutate live application state. The subtrees still render, their
 * components still fetch data, but their side-effect calls dead-end
 * at this boundary.
 *
 * No-throw when there's no parent env: returns an empty record so
 * `useEnvironment` consumers inside don't blow up either. That
 * matches the "inert by default" semantics of historical replay.
 */
export const InertEnvironmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const parent = React.useContext(EnvironmentContext);
    const inert = React.useMemo<Record<string, unknown>>(() => {
        if (!parent) return {};
        const out: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(parent)) {
            out[key] = typeof value === 'function' ? () => undefined : value;
        }
        return out;
    }, [parent]);
    return (
        <EnvironmentContext.Provider value={inert}>
            {children}
        </EnvironmentContext.Provider>
    );
};
